import type { CellValue, WorksheetRow } from "@zebrabi/office-data";
import { DataView, DataViewHierarchyLevel, DataViewMatrixNode, PrimitiveValue } from "@zebrabi/matrix-data";
import { MeasureRoles } from "@zebrabi/data-helpers/fieldAssignment";
import { getMatrixColumnsChildren, getMatrixRowsRecursiveChildrenForRange } from "@zebrabi/data-helpers/hierarchyParsing";
import { classifySourceColumns, isExcelTextCell } from "@zebrabi/data-helpers/sourceColumns";

/** Counts the leading columns whose every cell is text; those are the category columns, the rest are measures. */
export function getNumberOfCategoryColumns(data: Excel.Range): number {
    const columnCount = data.columnCount;

    let numberOfTextColumns = 0;
    for (let i = 0; i < columnCount; i++) {
        const colValues = data.values.map((row) => row[i]);
        const isStringCol = colValues.every((cell) => typeof cell === "string");

        if (isStringCol) {
            numberOfTextColumns++;
        } else {
            break;
        }
    }

    return numberOfTextColumns;
}

/**
 * The category labels with each label blanked where it repeats the row above on the same level, which is the shape the
 * hierarchy parser reads. Empty when the first two rows differ on the top level: the labels are already in that shape.
 */
export function handleRepeatedCategories(categoryLabelsValues: CellValue[][], categoryColumnsCount: number): CellValue[][] {
    const categoryLabels: CellValue[][] = [];
    if (categoryLabelsValues[0][0] === categoryLabelsValues[1][0]) {
        // remove repeated hierarchy categories
        const currentHierarchy: CellValue[] = [];

        categoryLabelsValues.forEach((row) => {
            const rowLabels: CellValue[] = [];
            for (let i = 0; i < categoryColumnsCount; i++) {
                const cell = row[i];
                if (currentHierarchy?.[i] === cell) {
                    rowLabels.push("");
                } else {
                    rowLabels.push(cell);
                    if (currentHierarchy?.[i] !== undefined) {
                        currentHierarchy[i] = cell;
                    } else {
                        currentHierarchy.push(cell);
                    }
                }
            }
            categoryLabels.push(rowLabels);
        });
    }
    return categoryLabels;
}

export function parseRangeDataView(
    dataView: DataView,
    rowLabelsValues: any[][],
    columnLabelsValues: any[][],
    dataValues: any[][],
    range: Excel.Range,
    sourceValues: readonly CellValue[][]
): DataView {
    dataView.matrix = { rows: null, columns: null, valueSources: [] };
    //rows
    const categoryColumns = dataView.metadata.columns.filter((f) => f.roles["Category"]);

    dataView.matrix.rows = {
        levels: categoryColumns.map((cc) => {
            return { sources: [cc] };
        }),
        root: {
            children: getMatrixRowsRecursiveChildrenForRange(0, rowLabelsValues.length - 1, 0, rowLabelsValues, dataValues, range),
        },
    };

    //columns
    const valueColumns = dataView.metadata.columns.filter(
        (f) =>
            f.roles[MeasureRoles.Values]
            || f.roles[MeasureRoles.PreviousYear]
            || f.roles[MeasureRoles.Plan]
            || f.roles[MeasureRoles.Forecast]
            || f.roles[MeasureRoles.Comments]
    );
    const groupColumns = dataView.metadata.columns.filter((f) => f.roles["Group"]);
    const levels: DataViewHierarchyLevel[] = [];
    if (groupColumns.length) {
        groupColumns.forEach((gc) => {
            levels.push({ sources: [gc] });
        });
    }
    if (!levels.length || valueColumns.length > 1) {
        levels.push({ sources: valueColumns });
    }
    dataView.matrix.columns = {
        levels: levels,
        root: {
            children: getMatrixColumnsChildren(valueColumns, groupColumns, columnLabelsValues, dataView.rowGrandTotal),
        },
    };

    dataView.matrix.valueSources = valueColumns;

    dataView.sourceColumns = classifySourceColumns(sourceValues[0], sourceValues.slice(1), isExcelTextCell);

    return dataView;
}

/** A DataView parsed as a cross table carries a Group column in its metadata. */
export function isCrossTablesDataView(dataView: DataView): boolean {
    return dataView.metadata.columns.some((col) => col.roles?.Group);
}

/**
 * The rows written back to the worksheet as sample data. A cross table writes one column per group; anything else writes
 * the category columns (up to three hierarchy levels) followed by one column per measure.
 */
export function dataViewToWorksheetRows(dataView: DataView): WorksheetRow[] {
    const data: WorksheetRow[] = [];
    if (isCrossTablesDataView(dataView)) {
        data.push(["Category", ...dataView.matrix.columns.root.children.map((c: DataViewMatrixNode) => toCell(c.value))]);
        dataView.matrix?.rows?.root?.children.forEach((c: DataViewMatrixNode) => {
            const rowData: WorksheetRow = [toCell(c.value)];
            dataView.matrix.columns.root.children.forEach((v: DataViewMatrixNode, j: number) => {
                rowData.push(toCell(c.values[j].value));
            });
            data.push(rowData);
        });
        return data;
    }

    const categoryFields = dataView.rowFields?.length > 0 ? dataView.rowFields : ["Category"];
    data.push([
        ...categoryFields,
        ...dataView.valueFields.map((field: string | Excel.PivotField) => (typeof field === "string" ? field : field.name)),
    ]);

    dataView.matrix?.rows?.root?.children.forEach((c: DataViewMatrixNode) => {
        // handle hierarchies (2 levels)
        if (c.children) {
            c.children.forEach((cc: DataViewMatrixNode) => {
                if (cc.children) {
                    // 3 levels
                    cc.children.forEach((ccc: DataViewMatrixNode) => {
                        const rowData: WorksheetRow = [toCell(c.value), toCell(cc.value), toCell(ccc.value)];
                        dataView.valueFields.forEach((v: string | Excel.PivotField, l: number) => {
                            rowData.push(toCell(ccc.values[l].value));
                        });
                        data.push(rowData);
                    });
                } else {
                    const rowData: WorksheetRow = [toCell(c.value), toCell(cc.value)];
                    dataView.valueFields.forEach((v: string | Excel.PivotField, k: number) => {
                        rowData.push(toCell(cc.values[k].value));
                    });
                    data.push(rowData);
                }
            });
        } else {
            const rowData: WorksheetRow = [toCell(c.value)];
            dataView.valueFields.forEach((v: string | Excel.PivotField, j: number) => {
                rowData.push(toCell(c.values[j].value));
            });
            data.push(rowData);
        }
    });
    return data;
}

/** A matrix value as a worksheet cell: a date is written as its local text, a missing value as an empty cell */
function toCell(value: PrimitiveValue | undefined): CellValue | null {
    if (value === undefined || value === null) {
        return null;
    }
    return value instanceof Date ? value.toLocaleDateString() : value;
}
